import CollectionRecord from '@packages/systems/dynamo/records/CollectionRecord';
import Field from '@packages/systems/dynamo/records/Field';
import {List, Map} from 'immutable';

import type {
  BindingContextDef,
  BindingContextTypeDef,
  BindingContextRelationshipDef,
  BindablePropertiesDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';

import {
  SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_ID,
  SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_TYPE_ID,
  SITE_USER_ACCOUNT_PAGE_BINDING_CONTEXT_ID,
  SITE_USER_ACCOUNT_PAGE_TYPE_ID,
} from './identifiers';

import {qualify} from '@packages/systems/wfdl-Qualified';
import {Plugins, Component} from '@packages/systems/wfdl';
import {USYS_PAGE_SETTINGS} from '@packages/systems/users/constants';

export const USER_SUBSCRIPTION_COLLECTION_ID = 'user-subscriptions';

export const USER_SUBSCRIPTION_COLLECTION = CollectionRecord({
  _id: USER_SUBSCRIPTION_COLLECTION_ID,
  slug: 'user-subscriptions',
  name: 'User Subscriptions',
  fields: List.of(
    Field({
      name: 'Name',
      slug: 'productName',
      type: 'PlainText',
    }),
    Field({
      name: 'Price',
      slug: 'variantPrice',
      type: 'CommercePrice',
    }),
    Field({
      name: 'Product Image',
      slug: 'variantImage',
      type: 'ImageRef',
    }),
    Field({
      name: 'Status',
      slug: 'status',
      type: 'Option',
      validations: Map<'options', List<Map<string, string>>>({
        options: List([
          Map({id: 'active', name: 'Active'}),
          Map({id: 'canceled', name: 'Canceled'}),
        ]),
      }),
    }),
    Field({
      name: 'Last Billing',
      slug: 'lastInvoiced',
      type: 'Date',
    }),
    Field({
      name: 'Next Billing',
      slug: 'periodEnd',
      type: 'Date',
    }),
    Field({
      name: 'Purchase Date',
      slug: 'subCreatedOn',
      type: 'Date',
    }),
    Field({
      name: 'Canceled On',
      slug: 'canceledOn',
      type: 'Date',
    }),
    Field({
      name: 'Addressee',
      slug: 'billingAddressAddressee',
      type: 'PlainText',
    }),
    Field({
      name: 'Line 1',
      slug: 'billingAddressLine1',
      type: 'PlainText',
    }),
    Field({
      name: 'Line 2',
      slug: 'billingAddressLine2',
      type: 'PlainText',
    }),
    Field({
      name: 'City',
      slug: 'billingAddressCity',
      type: 'PlainText',
    }),
    Field({
      name: 'State',
      slug: 'billingAddressState',
      type: 'PlainText',
    }),
    Field({
      name: 'Postal Code',
      slug: 'billingAddressPostalCode',
      type: 'PlainText',
    }),
    Field({
      name: 'Country',
      slug: 'billingAddressCountry',
      type: 'PlainText',
    }),
    Field({
      name: 'Card Expires Year',
      slug: 'cardExpiresYear',
      type: 'PlainText',
    }),
    Field({
      name: 'Card Expires Month',
      slug: 'cardExpiresMonth',
      type: 'PlainText',
    }),
    Field({
      name: 'Card Last 4',
      slug: 'cardLast4',
      type: 'PlainText',
    })
  ),
  pluralName: 'User Subscriptions',
  singularName: 'User Subscription',
});

export function defineSiteUserTypes(): Array<BindingContextTypeDef> {
  return [
    {
      entity: 'PageRoot',
      id: SITE_USER_ACCOUNT_PAGE_TYPE_ID,
      label: 'User Account Page',
    },
    {
      entity: 'Entity',
      id: SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_TYPE_ID,
      label: 'Subscription Item',
      iconName: 'product',
      externalKey: SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_TYPE_ID,
    },
  ];
}

export function defineSiteUserBindingContexts<RootTRef, EntityTRef, ListTRef>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): Array<BindingContextDef<RootTRef, EntityTRef, ListTRef>> {
  return [
    {
      variant: 'Page',
      id: SITE_USER_ACCOUNT_PAGE_BINDING_CONTEXT_ID,
      label: 'User Account Page',
      type: typeReferenceBuilder.entityOf({
        entity: 'PageRoot',
        id: SITE_USER_ACCOUNT_PAGE_TYPE_ID,
      }),
      matchPage: (page) =>
        page.utilKey === USYS_PAGE_SETTINGS.userAccount.utilKey,
    },
    {
      variant: 'List',
      id: SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_ID,
      label: 'User Subscription',
      type: typeReferenceBuilder.listOf({
        entity: 'Entity',
        id: SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_TYPE_ID,
      }),
      matchElement: {
        elementType: qualify(
          Plugins.name('Users'),
          Component.name('UserAccountSubscriptionListItem')
        ),
      },
    },
  ];
}

export function defineSiteUserRelationships<RootTRef, EntityTRef, ListTRef>(
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): Array<BindingContextRelationshipDef<RootTRef, EntityTRef>> {
  return [
    {
      type: referenceBuilder.entityOf({
        entity: 'PageRoot',
        id: SITE_USER_ACCOUNT_PAGE_TYPE_ID,
      }),
      relativeContexts: [SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_ID],
    },
  ];
}

export function defineSiteUserBindableProperties<
  RootTRef,
  EntityTRef,
  ListTRef,
>(
  typeReferenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): Array<BindablePropertiesDef<EntityTRef, ListTRef>> {
  return [
    // User subscriptions
    {
      type: typeReferenceBuilder.entityOf({
        entity: 'Entity',
        id: SITE_USER_SUBSCRIPTION_BINDING_CONTEXT_TYPE_ID,
      }),
      bindableProperties: [
        {
          id: 'subscription-status',
          type: 'Option',
          label: 'Status',
          path: [{slug: 'status', type: 'Option'}],
          queryPath: [],
          meta: {
            required: true,
            options: [
              {id: 'active', name: 'Active'},
              {id: 'canceled', name: 'Canceled'},
            ],
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-last-charge',
          type: 'Date',
          label: 'Last Billing',
          path: [{id: '', slug: 'lastInvoiced', type: 'Date'}],
          queryPath: [],
          meta: {
            format: 'date',
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-next-billing',
          type: 'Date',
          label: 'Next Billing',
          path: [{id: '', slug: 'periodEnd', type: 'Date'}],
          queryPath: [],
          meta: {
            format: 'date',
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-purchase-date',
          type: 'Date',
          label: 'Purchase Date',
          path: [{id: '', slug: 'subCreatedOn', type: 'Date'}],
          queryPath: [],
          meta: {
            format: 'date',
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-canceled-on',
          type: 'Date',
          label: 'Canceled On',
          path: [{id: '', slug: 'canceledOn', type: 'Date'}],
          queryPath: [],
          meta: {
            format: 'date',
            required: false,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-product-name',
          type: 'PlainText',
          label: 'Name',
          path: [{id: '', slug: 'productName', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-product-image',
          type: 'ImageRef',
          label: 'Product Image',
          path: [{id: '', slug: 'variantImage', type: 'ImageRef'}],
          queryPath: [],
          meta: {
            required: false,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-product-price',
          type: 'CommercePrice',
          label: 'Price',
          path: [{id: '', slug: 'variantPrice', type: 'CommercePrice'}],
          queryPath: [],
          meta: {required: true},
          rendering: {strategy: 'client'},
          groupLabel: 'Subscription',
        },
        {
          id: 'subscription-order-billing-addressee',
          type: 'PlainText',
          label: 'Addressee',
          path: [{id: '', slug: 'billingAddressAddressee', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-billing-city',
          type: 'PlainText',
          label: 'City',
          path: [{id: '', slug: 'billingAddressCity', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-billing-country',
          type: 'PlainText',
          label: 'Country',
          path: [{id: '', slug: 'billingAddressCountry', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-billing-line-1',
          type: 'PlainText',
          label: 'Line 1',
          path: [{id: '', slug: 'billingAddressLine1', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-billing-line-2',
          type: 'PlainText',
          label: 'Line 2',
          path: [{id: '', slug: 'BillingAddressLine2', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-billing-spostal-code',
          type: 'PlainText',
          label: 'Postal Code',
          path: [{id: '', slug: 'billingAddressPostalCode', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-billing-state',
          type: 'PlainText',
          label: 'State',
          path: [{id: '', slug: 'billingAddressState', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Billing Address',
        },
        {
          id: 'subscription-order-card-expiry-month',
          type: 'PlainText',
          label: 'Card Expires Month',
          path: [{id: '', slug: 'cardExpiresMonth', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Payment Details',
        },
        {
          id: 'subscription-order-card-expiry-year',
          type: 'PlainText',
          label: 'Card Expires Year',
          path: [{id: '', slug: 'cardExpiresYear', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Payment Details',
        },
        {
          id: 'subscription-order-card-last4',
          type: 'PlainText',
          label: 'Card Last 4',
          path: [{id: '', slug: 'cardLast4', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Payment Details',
        },
        {
          id: 'subscription-order-card-provider',
          type: 'PlainText',
          label: 'Card Provider',
          path: [{id: '', slug: 'cardProvider', type: 'PlainText'}],
          queryPath: [],
          meta: {
            singleLine: true,
            minLength: null,
            maxLength: null,
            required: true,
          },
          rendering: {strategy: 'client'},
          groupLabel: 'Payment Details',
        },
      ],
    },
  ];
}
